<template>
  <v-col :style="forPartyEdit ? 'padding: 0px;' : ''">
    <v-form
      ref="form"
      @submit.prevent="submitForm"
    >
      <div :class="forPartyEdit? 'px-2' : 'pa-2'">
        <v-row align="start">
          <v-col
            class="pr-6"
            cols="4"
          >
            <v-row>
              <v-autocomplete
                v-model="selectedScheme"
                :disabled="readOnly"
                :items="optionsDateFormat"
                item-text="title"
                item-value="id"
                label="Scheme"
                outlined
                return-object
                @change="onSchemeSelected"
              />
            </v-row>
          </v-col>
          <v-col
            cols="6"
          >
            <v-row>
              <v-menu
                ref="menu"
                v-model="menuOpen"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template #activator="{ on }">
                  <v-text-field
                    ref="dateField"
                    v-model="formatableDateFormatted"
                    :filled="!outlined"
                    :outlined="outlined"
                    :disabled="activeDateField"
                    :hint="hintText"
                    :label="calculatedLabel"
                    :placeholder="placeholderText"
                    persistent-hint
                    :rules="rules.date"
                    :style="forPartyEdit ? 'width:80%' : ''"
                    v-on="on"
                    @change="parseInputedDate"
                    @focus="focusOpen"
                    @keydown.tab="onTabOut"
                    @input="closeDatepickerMenu"
                  />
                </template>
                <v-date-picker
                  v-if="datePickable"
                  v-model="dateInput"
                  @input="closeDatepickerMenu"
                />
              </v-menu>
              <v-checkbox
                ref="checkboxField"
                v-model="boxChecked"
                class="ml-2 shrink"
                hide-details
                :disabled="disabled"
                :rules="rules.uncertain"
              />
            </v-row>
          </v-col>
        </v-row>
      </div>
    </v-form>
  </v-col>
</template>

<script>
import { isValidDay, isValidMonth } from '@/utilities'
import dateFieldsMixin from '@/mixins/dateFields.js'
import moment from 'moment'

const FORMAT_MMDDYYYY = 'MM/DD/YYYY'
const FORMAT_MMYYYY = 'MM/YYYY'
const FORMAT_YYYY = 'YYYY'

const FORMAT_YYYY_MM_DD = 'YYYY-MM-DD'

export default {
  name: 'FormatSelectableComboDateField',
  mixins: [dateFieldsMixin],

  props: {
    disabled: {
      type: Boolean,
      default: false,
    },

    errorMessages: {
      type: Array,
      default: () => [],
    },

    forPartyEdit: {
      type: Boolean,
      default: false,
    },

    format: {
      type: String,
      default: '',
    },

    formatableDate: {
      type: String,
      default: '',
    },

    inactive: {
      type: Boolean,
      default: false,
    },

    label: {
      type: String,
      required: true,
    },

    outlined: {
      type: Boolean,
      default: false,
    },

    uncertain: {
      type: Boolean,
      default: false,
    },
  },

  data () {
    return {
      formatableDateFormatted: '',
      menuOpen: false,
      optionsDateFormat: [
        { format: FORMAT_MMDDYYYY, id: 1, title: 'Month-Day-Year', value: null },
        { format: FORMAT_MMYYYY, id: 2, title: 'Month-Year', value: null },
        { format: FORMAT_YYYY, id: 3, title: 'Year', value: null },
        { format: FORMAT_MMDDYYYY, id: 4, title: 'Today', value: moment().format(FORMAT_YYYY_MM_DD) },
      ],

      rules: {
        date: [],
        uncertain: [],
      },

      selectedScheme: null,
    }
  },

  computed: {
    activeDateField () {
      return !this.selectedScheme || this.readOnly || this.selectedScheme?.title === 'Today'
    },

    boxChecked: {
      get () {
        return this.uncertain === true
      },

      set (newValue) {
        this.$emit('update-box', newValue)
      },
    },

    calculatedLabel () {
      return this.boxChecked && this.date ? `${this.label} (estimated)` : this.label
    },

    dateInput: {
      get () {
        return this.formatableDate
      },

      set (newDate) {
        this.formatableDateFormatted = moment(newDate).format(FORMAT_MMDDYYYY)
        this.parseInputedDate()
      },
    },

    datePickable () {
      return this.selectedScheme?.title === 'Month-Day-Year'
    },

    hintText () {
      if (this.errorMessages.length > 0) return this.errorMessages[0]
      return 'Check box if an ' + (this.formatableDate ? 'Estimated' : 'Unknown') + ' date.'
    },

    placeholderText () {
      return this.uncertainDate ? 'Unknown' : (this.selectedScheme?.format || FORMAT_MMDDYYYY)
    },

    readOnly () {
      return this.uncertainDate || this.inactive || this.disabled
    },
  },

  watch: {
    formatableDate (val) {
      this.formatableDateFormatted = this.formatInputDate(val)
    },
  },

  created () {
    let format = this.format
    const optionsDateFormat = [FORMAT_MMDDYYYY, FORMAT_MMYYYY, FORMAT_YYYY]
    if (!optionsDateFormat.includes(format)) {
      format = FORMAT_MMDDYYYY
      if (this.dateInput) this.parseInputedDate()
    }

    this.selectedScheme = this.optionsDateFormat.find(x => x.format === format)
    this.formatableDateFormatted = this.formatInputDate(this.dateInput)
  },

  methods: {
    closeDatepickerMenu () {
      this.menuOpen = false
    },

    formatInputDate (date) {
      if (!date || !this.selectedScheme || !this.selectedScheme.format) return date
      return moment(date).format(this.selectedScheme.format)
    },

    onSchemeSelected () {
      this.formatableDateFormatted = this.selectedScheme.value
      this.parseInputedDate()
    },

    onTabOut () {
      this.closeDatepickerMenu()
      this.$emit('tab-out')
    },

    parseInputedDate () {
      let date = null
      const format = this.selectedScheme ? this.selectedScheme.format : null

      switch (format) {
        case FORMAT_YYYY:
          date = this.processYearFormatDate(this.formatableDateFormatted)
          this.formatableDateFormatted = this.formatInputDate(date)
          break
        case FORMAT_MMYYYY:
          date = this.processMonthYearFormatDate(this.formatableDateFormatted)
          this.formatableDateFormatted = this.formatInputDate(date)
          break
        case FORMAT_MMDDYYYY:
          date = this.processMonthDayYearFormatDate(this.formatableDateFormatted)
          this.formatableDateFormatted = this.formatInputDate(date)
          break
        default:
          this.formatableDateFormatted = null
          break
      }
      if (format) this.updateDate(date)
    },

    processMonthDayYearFormat (date, type, delimiter) {
      if (!date) return null

      let monthDayYear = null
      let month = null
      let day = null
      let year = null
      date = date.replace(' ', '')
      if (date.length !== type) return null

      if (delimiter) {
        monthDayYear = date.slice(0, type).split(delimiter)
        month = monthDayYear[0]
        day = monthDayYear[1]
        year = monthDayYear[2]
      } else {
        monthDayYear = date.slice(0, 8)
        month = monthDayYear.slice(0, 2)
        day = monthDayYear.slice(2, 4)
        year = monthDayYear.slice(4, 8)
      }

      if (/\d{4}$/.test(year.trim()) && isValidMonth(month) && isValidDay(day)) {
        return `${year}-${month}-${day}`
      } else {
        return null
      }
    },

    processMonthDayYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.includes('-')) {
        return this.processMonthDayYearFormat(date, 10, '-')
      } else if (date.includes('/')) {
        return this.processMonthDayYearFormat(date, 10, '/')
      } else if (date.length === 8) {
        return this.processMonthDayYearFormat(date, 8)
      } else {
        return null
      }
    },

    processMonthYearFormat (date, type, delimiter) {
      if (!date) return null

      let monthYear = null
      let month = null
      let year = null
      date = date.replace(' ', '')
      if (date.length !== type) return null

      if (delimiter) {
        monthYear = date.slice(0, type).split(delimiter)
        month = monthYear[0]
        year = monthYear[1]
      } else {
        monthYear = date.slice(0, 6)
        month = monthYear.slice(0, 2)
        year = monthYear.slice(2, 6)
      }

      if (/\d{4}$/.test(year.trim()) && isValidMonth(month)) {
        return `${year}-${month}-01`
      } else {
        return null
      }
    },

    processMonthYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.includes('-')) {
        return this.processMonthYearFormat(date, 7, '-')
      } else if (date.includes('/')) {
        return this.processMonthYearFormat(date, 7, '/')
      } else if (date.length === 6) {
        return this.processMonthYearFormat(date, 6)
      } else {
        return null
      }
    },

    processYearFormat (date, type) {
      if (!date) return null

      date = date.replace(' ', '')
      if (!/\d{4}$/.test(date.trim())) return null

      return `${date}-01-01`
    },

    processYearFormatDate (date) {
      if (!date) return null

      date = date.replace(' ', '')
      if (date.length === 4) {
        return this.processYearFormat(date, 4)
      } else {
        return null
      }
    },

    submitForm () {
      this.$emit('submit')
    },

    updateDate (date) {
      this.$emit('update-date', { date, format: this.selectedScheme?.format })
    },
  },
}
</script>
